// frontend/src/components/landing-page/pages/PaymentInfo.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  FiCreditCard, 
  FiLock, 
  FiCheck, 
  FiArrowLeft, 
  FiShield,
  FiInfo
} from 'react-icons/fi';
import poslahLogo from '../../../assets/poslahlogo.png';
import { subscriptionService } from '../../../services/subscriptionService';
import ThankYou from '../alertpage/ThankYou';

import '../../../css/auth-pages.css';
import '../../../css/paymentinfo.css';

const MotionDiv = motion.div;
const MotionButton = motion.button;

const PaymentInfo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [registrationData, setRegistrationData] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [pricingData, setPricingData] = useState(null);
  const [errors, setErrors] = useState({});
  const [showThankYou, setShowThankYou] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);

  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardName: '',
    billingAddress: {
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    saveCard: false,
    agreeToTerms: false
  });

  useEffect(() => {
    if (location.state) {
      setSelectedPlan(location.state.plan);
      setSelectedCountry(location.state.country);
      setRegistrationData(location.state.registrationData);
      
      const planId = location.state.plan.plan_id;
      const countryId = location.state.country.country_id;
      
      const loadPricing = async () => {
        try {
          const pricing = await subscriptionService.calculatePricing(planId, countryId, billingCycle);
          setPricingData(pricing);
        } catch (error) {
          console.error('Error calculating pricing:', error);
        }
      };
      
      loadPricing();
    } else {
      navigate('/get-started');
    }
  }, [location.state, navigate, billingCycle]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setPaymentData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setPaymentData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const formatExpiryDate = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + (v.length > 2 ? '/' + v.substring(2, 4) : '');
    }
    return v;
  };

  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.target.value);
    setPaymentData(prev => ({ ...prev, cardNumber: formatted }));
  };

  const handleExpiryChange = (e) => {
    const formatted = formatExpiryDate(e.target.value);
    setPaymentData(prev => ({ ...prev, expiryDate: formatted }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!paymentData.cardNumber || paymentData.cardNumber.replace(/\s/g, '').length < 16) {
      newErrors.cardNumber = 'Please enter a valid card number';
    }

    if (!paymentData.expiryDate || paymentData.expiryDate.length < 5) {
      newErrors.expiryDate = 'Please enter a valid expiry date';
    }

    if (!paymentData.cvv || paymentData.cvv.length < 3) {
      newErrors.cvv = 'Please enter a valid CVV';
    }

    if (!paymentData.cardName.trim()) {
      newErrors.cardName = 'Please enter the cardholder name';
    }

    if (!paymentData.billingAddress.address.trim()) {
      newErrors['billingAddress.address'] = 'Billing address is required';
    }

    if (!paymentData.billingAddress.city.trim()) {
      newErrors['billingAddress.city'] = 'City is required';
    }

    if (!paymentData.billingAddress.zipCode.trim()) {
      newErrors['billingAddress.zipCode'] = 'ZIP/Postal code is required';
    }

    if (!paymentData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBillingCycleChange = async (cycle) => {
    setBillingCycle(cycle);
    if (selectedPlan && selectedCountry) {
      try {
        const pricing = await subscriptionService.calculatePricing(
          selectedPlan.plan_id, 
          selectedCountry.country_id, 
          cycle
        );
        setPricingData(pricing);
      } catch (error) {
        console.error('Error calculating pricing:', error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    
    try {
      // Simulate payment processing
      const transactionId = `TXN_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const finalAmount = billingCycle === 'yearly' ? 
        (pricingData?.yearlyPrice || selectedPlan.yearly_price) : 
        (pricingData?.monthlyPrice || selectedPlan.monthly_price);
      
      const paymentProcessResult = {
        success: true,
        transactionId,
        amount: finalAmount,
        billingCycle
      };
      
      if (paymentProcessResult.success) {
        // Now process the complete registration with payment data
        const registrationPayload = {
          ...registrationData,
          paymentData: {
            transactionId: paymentProcessResult.transactionId,
            amount: paymentProcessResult.amount,
            billingCycle: paymentProcessResult.billingCycle
          },
          billingCycle: paymentProcessResult.billingCycle
        };
        
        const registrationResult = await subscriptionService.processPaymentAndRegister(registrationPayload);
        
        if (registrationResult.success) {
          setPaymentResult({
            transactionId: paymentProcessResult.transactionId,
            amount: finalAmount,
            token: registrationResult.data.token,
            user: registrationResult.data.user
          });
          setShowThankYou(true);
        }
      }
    } catch (err) {
      console.error('Payment failed:', err);
      setErrors({ submit: 'Payment failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleThankYouClose = () => {
    setShowThankYou(false);
    // Store token and redirect to fresh dashboard
    if (paymentResult?.token) {
      localStorage.setItem('token', paymentResult.token);
      localStorage.setItem('user', JSON.stringify(paymentResult.user));
      // Redirect to dashboard without existing data
      window.location.href = '/dashboard?fresh=true';
    }
  };

  if (!selectedPlan || !selectedCountry || !registrationData) {
    return <div>Loading...</div>;
  }

  const monthlyPrice = pricingData?.monthlyPrice || selectedPlan.monthly_price;
  const yearlyPrice = pricingData?.yearlyPrice || selectedPlan.yearly_price;
  const finalAmount = billingCycle === 'yearly' ? yearlyPrice : monthlyPrice;
  const savings = billingCycle === 'yearly' ? (monthlyPrice * 12) - yearlyPrice : 0;

  return (
    <div className="payment-page">
      <div className="animated-bg"></div>
      
      <MotionDiv 
        className="payment-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Header */}
        <div className="payment-header">
          <Link to="/" className="logo-link">
            <img src={poslahLogo} alt="POSlah Logo" className="payment-logo" />
          </Link>
          <button className="back-button" onClick={() => navigate(-1)}>
            <FiArrowLeft /> Back
          </button>
        </div>

        <div className="payment-content">
          {/* Left Side - Plan Summary */}
          <MotionDiv className="plan-summary-card">
            <div className="plan-summary-header">
              <h2>Order Summary</h2>
              <div className="security-badge">
                <FiShield />
                <span>Secure Payment</span>
              </div>
            </div>

            <div className="selected-plan">
              <div className="plan-info">
                <h3>{selectedPlan.plan_name} Plan</h3>
                <p className="plan-description">
                  Perfect for {selectedPlan.plan_name.toLowerCase()} businesses
                </p>
              </div>

              <div className="billing-toggle">
                <div className="toggle-buttons">
                  <button
                    className={`toggle-btn ${billingCycle === 'monthly' ? 'active' : ''}`}
                    onClick={() => handleBillingCycleChange('monthly')}
                  >
                    Monthly
                  </button>
                  <button
                    className={`toggle-btn ${billingCycle === 'yearly' ? 'active' : ''}`}
                    onClick={() => handleBillingCycleChange('yearly')}
                  >
                    Yearly
                    {savings > 0 && <span className="savings-badge">Save {selectedCountry.currency_symbol}{savings.toFixed(2)}</span>}
                  </button>
                </div>
              </div>

              <div className="price-breakdown">
                <div className="price-row">
                  <span>{selectedPlan.plan_name} Plan ({billingCycle})</span>
                  <span>{selectedCountry.currency_symbol}{finalAmount}</span>
                </div>
                
                {selectedPlan.trial_days > 0 && (
                  <div className="trial-info">
                    <FiInfo />
                    <span>{selectedPlan.trial_days} days free trial included</span>
                  </div>
                )}

                <div className="price-total">
                  <span>Total</span>
                  <span className="total-amount">{selectedCountry.currency_symbol}{finalAmount}</span>
                </div>
              </div>

              <div className="plan-features">
                <h4>What's included:</h4>
                <ul>
                  <li><FiCheck /> Up to {selectedPlan.max_branches === -1 ? 'Unlimited' : selectedPlan.max_branches} branches</li>
                  <li><FiCheck /> Up to {selectedPlan.max_users === -1 ? 'Unlimited' : selectedPlan.max_users} users</li>
                  <li><FiCheck /> Up to {selectedPlan.max_products === -1 ? 'Unlimited' : selectedPlan.max_products} products</li>
                  {selectedPlan.features && (typeof selectedPlan.features === 'string' ? JSON.parse(selectedPlan.features) : selectedPlan.features).ai_insights && (
                    <li><FiCheck /> AI Insights & Analytics</li>
                  )}
                </ul>
              </div>
            </div>
          </MotionDiv>

          {/* Right Side - Payment Form */}
          <MotionDiv className="payment-form-card">
            <div className="payment-form-header">
              <h2>Payment Information</h2>
              <div className="security-info">
                <FiLock />
                <span>Your payment information is secure and encrypted</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="payment-form">
              {/* Card Information */}
              <div className="form-section">
                <h3 className="section-title">
                  <FiCreditCard />
                  Card Information
                </h3>

                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>Card Number</label>
                    <input
                      type="text"
                      name="cardNumber"
                      className={`form-input ${errors.cardNumber ? 'error' : ''}`}
                      placeholder="1234 5678 9012 3456"
                      value={paymentData.cardNumber}
                      onChange={handleCardNumberChange}
                      maxLength={19}
                    />
                    {errors.cardNumber && <span className="error-text">{errors.cardNumber}</span>}
                  </div>

                  <div className="form-group">
                    <label>Expiry Date</label>
                    <input
                      type="text"
                      name="expiryDate"
                      className={`form-input ${errors.expiryDate ? 'error' : ''}`}
                      placeholder="MM/YY"
                      value={paymentData.expiryDate}
                      onChange={handleExpiryChange}
                      maxLength={5}
                    />
                    {errors.expiryDate && <span className="error-text">{errors.expiryDate}</span>}
                  </div>

                  <div className="form-group">
                    <label>CVV</label>
                    <input
                      type="text"
                      name="cvv"
                      className={`form-input ${errors.cvv ? 'error' : ''}`}
                      placeholder="123"
                      value={paymentData.cvv}
                      onChange={handleInputChange}
                      maxLength={4}
                    />
                    {errors.cvv && <span className="error-text">{errors.cvv}</span>}
                  </div>

                  <div className="form-group full-width">
                    <label>Cardholder Name</label>
                    <input
                      type="text"
                      name="cardName"
                      className={`form-input ${errors.cardName ? 'error' : ''}`}
                      placeholder="John Doe"
                      value={paymentData.cardName}
                      onChange={handleInputChange}
                    />
                    {errors.cardName && <span className="error-text">{errors.cardName}</span>}
                  </div>
                </div>
              </div>

              {/* Billing Address */}
              <div className="form-section">
                <h3 className="section-title">Billing Address</h3>

                <div className="form-grid">
                  <div className="form-group full-width">
                    <label>Address</label>
                    <input
                      type="text"
                      name="billingAddress.address"
                      className={`form-input ${errors['billingAddress.address'] ? 'error' : ''}`}
                      placeholder="Street address"
                      value={paymentData.billingAddress.address}
                      onChange={handleInputChange}
                    />
                    {errors['billingAddress.address'] && <span className="error-text">{errors['billingAddress.address']}</span>}
                  </div>

                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      name="billingAddress.city"
                      className={`form-input ${errors['billingAddress.city'] ? 'error' : ''}`}
                      placeholder="City"
                      value={paymentData.billingAddress.city}
                      onChange={handleInputChange}
                    />
                    {errors['billingAddress.city'] && <span className="error-text">{errors['billingAddress.city']}</span>}
                  </div>

                  <div className="form-group">
                    <label>State/Province</label>
                    <input
                      type="text"
                      name="billingAddress.state"
                      className="form-input"
                      placeholder="State/Province"
                      value={paymentData.billingAddress.state}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>ZIP/Postal Code</label>
                    <input
                      type="text"
                      name="billingAddress.zipCode"
                      className={`form-input ${errors['billingAddress.zipCode'] ? 'error' : ''}`}
                      placeholder="12345"
                      value={paymentData.billingAddress.zipCode}
                      onChange={handleInputChange}
                    />
                    {errors['billingAddress.zipCode'] && <span className="error-text">{errors['billingAddress.zipCode']}</span>}
                  </div>

                  <div className="form-group">
                    <label>Country</label>
                    <input
                      type="text"
                      name="billingAddress.country"
                      className="form-input"
                      placeholder="Country"
                      value={selectedCountry.country_name}
                      readOnly
                    />
                  </div>
                </div>
              </div>

              {/* Checkboxes */}
              <div className="form-options">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="saveCard"
                    checked={paymentData.saveCard}
                    onChange={handleInputChange}
                  />
                  <span className="checkmark"></span>
                  Save card for future payments
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="agreeToTerms"
                    checked={paymentData.agreeToTerms}
                    onChange={handleInputChange}
                    className={errors.agreeToTerms ? 'error' : ''}
                  />
                  <span className="checkmark"></span>
                  I agree to the <Link to="/terms" target="_blank">Terms & Conditions</Link>
                </label>
                {errors.agreeToTerms && <span className="error-text">{errors.agreeToTerms}</span>}
              </div>

              {errors.submit && (
                <div className="error-message">
                  {errors.submit}
                </div>
              )}

              {/* Submit Button */}
              <MotionButton
                type="submit"
                className="payment-submit-btn"
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {loading ? (
                  <>
                    <div className="loading-spinner"></div>
                    Processing Payment...
                  </>
                ) : (
                  <>
                    <FiLock />
                    Pay {selectedCountry.currency_symbol}{finalAmount}
                  </>
                )}
              </MotionButton>

              <div className="payment-security">
                <p>🔒 Your payment is secured with 256-bit SSL encryption</p>
              </div>
            </form>
          </MotionDiv>
        </div>
      </MotionDiv>
      
      <ThankYou 
        isOpen={showThankYou}
        onClose={handleThankYouClose}
        paymentData={paymentResult}
        planData={selectedPlan}
        onNavigateToDashboard={handleThankYouClose}
      />
    </div>
  );
};

export default PaymentInfo;